import { ArrowRight, Award, BriefcaseBusiness, Check, History, ShieldCheck, X } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useGame } from '../game/GameContext';
import { loadHistory } from '../game/storage';
import type { CounselAvatar as CounselAvatarId } from '../game/types';
import { CounselAvatar } from './CounselAvatar';

const AVATARS: CounselAvatarId[] = ['cipher', 'oracle', 'vector', 'halo'];
const ACCENTS = ['#c99b58', '#75b7c2', '#a88dd4', '#d47b68'];

export function ProfileOverlay() {
  const { state, dispatch } = useGame();
  const [name, setName] = useState(state.profile.name);
  const [callsign, setCallsign] = useState(state.profile.callsign);
  const [avatar, setAvatar] = useState<CounselAvatarId>(state.profile.avatar);
  const [accent, setAccent] = useState(state.profile.accent);
  const isNew = !state.profile.created;
  const xpFloor = (state.profile.level - 1) * 900;
  const levelProgress = ((state.profile.xp - xpFloor) / 900) * 100;
  const recentRecords = state.profile.created ? loadHistory().slice(0, 3) : [];

  const submit = (event: FormEvent) => {
    event.preventDefault();
    dispatch({ type: 'SAVE_PROFILE', profile: { name, callsign, avatar, accent } });
    if (state.phase === 'title') dispatch({ type: 'START_CASE' });
  };

  return (
    <div className="overlay-backdrop profile-backdrop screen-layer">
      <section className="profile-panel panel-surface">
        <header className="profile-panel__header">
          <div>
            <span>TRIBUNAL COUNSEL REGISTRY</span>
            <h2>{isNew ? 'Forge your court identity' : 'Counsel record'}</h2>
          </div>
          <button className="icon-action" onClick={() => dispatch({ type: 'CLOSE_OVERLAY' })} aria-label="Close profile"><X /></button>
        </header>

        <div className="profile-panel__body">
          <aside className="profile-card-preview" style={{ '--profile-accent': accent } as React.CSSProperties}>
            <div className="profile-card-preview__scan" />
            <CounselAvatar avatar={avatar} accent={accent} />
            <span>{callsign || 'NIGHT COUNSEL'}</span>
            <strong>{name || 'Your name'}</strong>
            <small>{state.profile.created ? state.profile.counselId : 'ID SEALED ON REGISTRATION'}</small>
            <div>
              <b>LEVEL {state.profile.level.toString().padStart(2, '0')}</b>
              <i><em style={{ width: `${Math.max(4, levelProgress)}%` }} /></i>
            </div>
          </aside>

          <form onSubmit={submit}>
            <label>
              <span>COUNSEL NAME</span>
              <input value={name} onChange={(event) => setName(event.target.value)} maxLength={32} placeholder="e.g. Parham Veysi" autoFocus />
            </label>
            <label>
              <span>COURT CALLSIGN</span>
              <input value={callsign} onChange={(event) => setCallsign(event.target.value)} maxLength={22} placeholder="NIGHT COUNSEL" />
            </label>

            <fieldset>
              <legend>SIGNATURE GLYPH</legend>
              <div className="profile-choices">
                {AVATARS.map((item) => (
                  <button type="button" key={item} className={item === avatar ? 'is-selected' : ''} onClick={() => setAvatar(item)} aria-label={item}>
                    <CounselAvatar avatar={item} accent={accent} />
                    {item === avatar && <Check />}
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset>
              <legend>RECORD ACCENT</legend>
              <div className="profile-colors">
                {ACCENTS.map((color) => (
                  <button type="button" key={color} className={color === accent ? 'is-selected' : ''} style={{ background: color }} onClick={() => setAccent(color)} aria-label={color} />
                ))}
              </div>
            </fieldset>

            {state.profile.created && (
              <>
                <div className="profile-career">
                  <div><BriefcaseBusiness /><span>Cases</span><strong>{state.profile.casesCompleted}</strong></div>
                  <div><Award /><span>S ranks</span><strong>{state.profile.sRanks}</strong></div>
                  <div><ShieldCheck /><span>Best</span><strong>{state.profile.bestScore}%</strong></div>
                </div>
                {recentRecords.length > 0 && (
                  <section className="profile-records">
                    <header><History /><span>RECENT VERIFIED RECORDS</span></header>
                    {recentRecords.map((record) => (
                      <div key={record.resultHash}>
                        <strong>{record.rank}</strong>
                        <span>{record.verdict}<small>{record.routeTitle}</small></span>
                        <code>{record.resultHash}</code>
                      </div>
                    ))}
                  </section>
                )}
              </>
            )}

            <button className="primary-action primary-action--large" type="submit" disabled={!name.trim()}>
              {state.phase === 'title' ? 'Seal identity & open case' : 'Update counsel record'} <ArrowRight />
            </button>
            <p>Your profile and case history stay local to this device. No account or sign-in is required.</p>
          </form>
        </div>
      </section>
    </div>
  );
}
